import { useEffect, useState } from 'react';

import { ImageIcon } from '@radix-ui/react-icons';
import clsx from 'clsx';

import { createAxiosInstance } from '@src/api';
import { ReactComponent as ProcessorIcon } from '@src/assets/processor.svg';
import { Show } from '@src/hoc';

import styles from './styles.module.css';

async function fetchImage({ onSuccess, onError, controller, src }) {
  if (!src) return;

  try {
    const imgReq = createAxiosInstance();

    const result = await imgReq.get(src, {
      responseType: 'blob',
      signal: controller.signal
    });

    const reader = new FileReader();
    reader.readAsDataURL(result.data);
    reader.onloadend = function () {
      const base64data = reader.result;
      onSuccess(base64data);
    };
  } catch (error) {
    // removes this as it clutters sentry logs - this is generally triggered when ui actions are performed extremely fast or the map is unmounted quickly
    // console.error(error);
    onError?.(error);
  }
}

const Thumbnail = ({
  src,
  className,
  currentClasses,
  fallbackIcon: Icon,
  showTumbnail
}) => {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchImage({ onSuccess: data => setImgSrc(data), controller, src });

    return () => controller.abort();
  }, [src]);

  return (
    <div className={clsx(className, styles.ThumbContainer, currentClasses)}>
      <Show
        when={imgSrc !== null}
        fallback={
          <Show when={showTumbnail}>
            <Icon className={styles.icon} />
          </Show>
        }
      >
        <img src={imgSrc} className={styles.Img} />
      </Show>
    </div>
  );
};

export const CircularThumbnail = ({
  src,
  className = '',
  showTumbnail = true
}) => {
  return (
    <Thumbnail
      src={src}
      className={className}
      currentClasses={styles.thumbContainerCircle}
      fallbackIcon={ProcessorIcon}
      showTumbnail={showTumbnail}
    />
  );
};

export const VerticalThumbnail = ({
  src,
  className = '',
  showTumbnail = true
}) => {
  return (
    <Thumbnail
      src={src}
      className={className}
      currentClasses={styles.ThumbContainerRectangle}
      fallbackIcon={ProcessorIcon}
      showTumbnail={showTumbnail}
    />
  );
};

export const SquareThumbnail = ({
  src,
  className = '',
  showTumbnail = true
}) => {
  return (
    <Thumbnail
      src={src}
      className={className}
      currentClasses={styles.ThumbContainerSquare}
      fallbackIcon={ImageIcon}
      showTumbnail={showTumbnail}
    />
  );
};

export const LandscapeThumbnail = ({ src, className, showTumbnail = true }) => {
  return (
    <Thumbnail
      src={src}
      className={className}
      currentClasses={styles.ThumbContainerLandscape}
      fallbackIcon={ImageIcon}
      showTumbnail={showTumbnail}
    />
  );
};
